import { useEffect, useRef, useState, useCallback } from "react";
import { adminApi, formatAud } from "../../lib/api";
import {
  Send, RefreshCcw, Paperclip, FileText, X,
  Link2, Search, UserCheck, Trash2, AlertOctagon,
  ChevronDown, MessageSquare, Users, Inbox,
  ShieldAlert, Bell, ZoomIn, ExternalLink, ShoppingBag,
} from "lucide-react";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";

const MAX_FILE_BYTES = 4 * 1024 * 1024;

const TABS = [
  { key: "unread",     label: "New",        icon: Bell,         color: "#e31e24" },
  { key: "all",        label: "All",         icon: Inbox,        color: "#0b1a3b" },
  { key: "mine",       label: "Mine",        icon: UserCheck,    color: "#1e5bb8" },
  { key: "unassigned", label: "Unassigned",  icon: Users,        color: "#d97706" },
  { key: "spam",       label: "Spam",        icon: ShieldAlert,  color: "#64748b" },
];

function timeAgo(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60)    return "just now";
  if (diff < 3600)  return `${Math.floor(diff/60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff/3600)}h ago`;
  return new Date(iso).toLocaleDateString("en-AU", { day:"numeric", month:"short" });
}

// ─── Image lightbox ─────────────────────────────────────────────────
function Lightbox({ src, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background:"rgba(0,0,0,0.92)" }} onClick={onClose}>
      <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-full"
        style={{ background:"rgba(255,255,255,0.15)", color:"#fff" }}>
        <X className="h-5 w-5"/>
      </button>
      <img src={src} alt="" className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg"
        onClick={e => e.stopPropagation()}/>
      <a href={src} download className="absolute bottom-4 right-4 px-3 py-1.5 rounded-lg text-[12px] font-semibold"
        style={{ background:"rgba(255,255,255,0.15)", color:"#fff" }}
        onClick={e => e.stopPropagation()}>
        <ExternalLink className="h-3.5 w-3.5 inline mr-1"/>Download
      </a>
    </div>
  );
}

// ─── Message bubble ─────────────────────────────────────────────────
function Bubble({ m, onImageClick }) {
  const isBCP = m.sender === "admin";
  const isSys = m.sender === "system";

  if (isSys) return (
    <div className="flex justify-center my-2">
      <div className="text-[11px] px-4 py-1.5 rounded-full italic"
        style={{ background:"#fef3c7", border:"1px solid #fde68a", color:"#92400e" }}>
        🤖 {m.body}
      </div>
    </div>
  );

  return (
    <div className={`flex ${isBCP ? "justify-end" : "justify-start"} mb-2`}>
      <div className={`max-w-[72%] px-3 py-2.5 rounded-2xl text-[13px] ${isBCP ? "rounded-br-sm" : "rounded-bl-sm"}`}
        style={isBCP
          ? { background:"#0b1a3b", color:"#fff" }
          : { background:"#fff", color:"#0f172a", border:"1px solid #eef2f8", boxShadow:"0 1px 4px rgba(0,0,0,0.06)" }
        }>
        <div className="text-[10px] font-mono uppercase tracking-wider mb-1"
          style={{ color: isBCP ? "#93c5fd" : "#e31e24" }}>
          {isBCP ? (m.admin_name || "BCP Support") : (m.name || "Customer")}
        </div>
        {m.body && <div className="whitespace-pre-wrap break-words leading-relaxed">{m.body}</div>}

        {/* Attachments */}
        {m.attachments?.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {m.attachments.map((a, i) => a.kind === "image" ? (
              <button key={i} type="button" onClick={() => onImageClick(a.data_url)}
                className="relative rounded-lg overflow-hidden cursor-zoom-in group"
                style={{ width:72, height:72, flexShrink:0 }}>
                <img src={a.data_url} alt={a.name} className="w-full h-full object-cover"/>
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  style={{ background:"rgba(0,0,0,0.4)" }}>
                  <ZoomIn className="h-5 w-5 text-white"/>
                </div>
              </button>
            ) : (
              <a key={i} href={a.data_url} download={a.name}
                className="flex items-center gap-2 px-3 py-2 rounded-lg text-[12px]"
                style={{ background: isBCP ? "rgba(255,255,255,0.1)" : "#f5f7fb", border:"1px solid #eef2f8" }}>
                <FileText className="h-4 w-4 shrink-0" style={{ color: isBCP ? "#fff" : "#e31e24" }}/>
                <span className="truncate max-w-[120px]">{a.name}</span>
              </a>
            ))}
          </div>
        )}

        {/* Payment link card */}
        {m.payment_link?.url && (
          <a href={m.payment_link.url} target="_blank" rel="noreferrer"
            className="mt-2 block rounded-xl overflow-hidden"
            style={{ border: isBCP ? "1px solid rgba(255,255,255,0.2)" : "1px solid #1e5bb8" }}>
            <div className="px-3 py-2" style={{ background: isBCP ? "rgba(255,255,255,0.1)" : "#1e5bb8", color:"#fff" }}>
              <div className="text-[10px] font-mono uppercase opacity-80">Stripe payment</div>
              <div className="font-heading text-base font-bold">{formatAud(m.payment_link.amount)}</div>
              {m.payment_link.order_number && <div className="text-[11px] opacity-80">Order {m.payment_link.order_number}</div>}
            </div>
            <div className="px-3 py-1.5 flex items-center justify-between text-[11px]"
              style={{ background: isBCP ? "rgba(255,255,255,0.05)" : "#f0f4ff", color: isBCP ? "#fff" : "#0b1a3b" }}>
              Pay securely <ExternalLink className="h-3 w-3"/>
            </div>
          </a>
        )}

        <div className={`text-[10px] mt-1 ${isBCP ? "text-blue-300" : "text-zinc-400"}`}>
          {new Date(m.created_at).toLocaleTimeString("en-AU", { hour:"2-digit", minute:"2-digit" })}
        </div>
      </div>
    </div>
  );
}

// ─── Drop payment link modal ─────────────────────────────────────────
function PayLinkModal({ convId, onClose, onSent }) {
  const [orders,  setOrders]  = useState([]);
  const [search,  setSearch]  = useState("");
  const [sel,     setSel]     = useState(null);
  const [busy,    setBusy]    = useState(false);

  useEffect(() => {
    adminApi.get("/admin/orders?page_size=50&payment_status=pending").then(r => {
      setOrders(r.data.items || []);
    }).catch(() => {});
  }, []);

  const filtered = orders.filter(o =>
    o.order_number?.toLowerCase().includes(search.toLowerCase()) ||
    o.customer_name?.toLowerCase().includes(search.toLowerCase())
  );

  const send = async () => {
    if (!sel) return;
    setBusy(true);
    try {
      await adminApi.post(`/admin/messages/${convId}/drop-pay-link`, { order_id: sel.id });
      toast.success("Payment link sent!");
      onSent();
      onClose();
    } catch(e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background:"rgba(0,0,0,0.5)" }} onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl p-5 space-y-4"
        style={{ border:"1px solid #dde3ef" }} onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div className="font-heading font-bold text-[16px]" style={{ color:"#0f172a" }}>Send Payment Link</div>
          <button onClick={onClose}><X className="h-5 w-5" style={{ color:"#94a3b8" }}/></button>
        </div>
        <Input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search orders…" className="rounded-xl" style={{ borderColor:"#dde3ef" }}/>
        <div className="max-h-60 overflow-y-auto space-y-1.5">
          {filtered.map(o => (
            <button key={o.id} onClick={() => setSel(o)}
              className="w-full text-left px-3 py-2.5 rounded-xl transition-all"
              style={sel?.id === o.id
                ? { background:"#0b1a3b", color:"#fff" }
                : { background:"#f5f7fb", border:"1px solid #dde3ef", color:"#0f172a" }}>
              <div className="font-semibold text-[13px]">{o.order_number}</div>
              <div className="text-[11px] opacity-70">{o.customer_name} · {formatAud(o.total)}</div>
            </button>
          ))}
          {filtered.length === 0 && (
            <div className="text-center py-4 text-[12px]" style={{ color:"#94a3b8" }}>No unpaid orders found</div>
          )}
        </div>
        <button onClick={send} disabled={!sel || busy}
          className="w-full h-10 rounded-xl font-bold text-[13px] disabled:opacity-40"
          style={{ background:"#0b1a3b", color:"#ffffff" }}>
          {busy ? "Sending…" : "Send Payment Link"}
        </button>
      </div>
    </div>
  );
}

// ─── Delete confirmation ─────────────────────────────────────────────
function DeleteModal({ conv, onClose, onDeleted }) {
  const [busy, setBusy] = useState(false);
  const confirm = async () => {
    setBusy(true);
    try {
      await adminApi.delete(`/admin/messages/${conv.conversation_id}`);
      toast.success("Conversation deleted");
      onDeleted();
    } catch(e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background:"rgba(0,0,0,0.5)" }} onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-sm p-6 shadow-2xl text-center"
        style={{ border:"1px solid #dde3ef" }} onClick={e => e.stopPropagation()}>
        <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3"
          style={{ background:"#fef2f2" }}>
          <Trash2 className="h-6 w-6" style={{ color:"#e31e24" }}/>
        </div>
        <div className="font-heading font-bold text-[16px] mb-1" style={{ color:"#0f172a" }}>Delete Conversation?</div>
        <div className="text-[13px] mb-4" style={{ color:"#64748b" }}>
          All messages with <strong>{conv.name}</strong> will be permanently deleted.
        </div>
        <div className="flex gap-2">
          <button onClick={confirm} disabled={busy}
            className="flex-1 h-10 rounded-xl font-bold text-[13px] disabled:opacity-50"
            style={{ background:"#e31e24", color:"#ffffff" }}>
            {busy ? "Deleting…" : "Yes, Delete"}
          </button>
          <button onClick={onClose}
            className="flex-1 h-10 rounded-xl text-[13px] font-semibold"
            style={{ background:"#f5f7fb", border:"1px solid #dde3ef", color:"#374151" }}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main ────────────────────────────────────────────────────────────
export default function AdminMessages() {
  const [convos,     setConvos]    = useState([]);
  const [stats,      setStats]     = useState({ unread:0, mine:0, unassigned:0, spam:0, total:0 });
  const [active,     setActive]    = useState(null);
  const [thread,     setThread]    = useState([]);
  const [reply,      setReply]     = useState("");
  const [attachments,setAttach]    = useState([]);
  const [sending,    setSending]   = useState(false);
  const [tab,        setTab]       = useState("unread");
  const [search,     setSearch]    = useState("");
  const [staffList,  setStaffList] = useState([]);
  const [assignOpen, setAssignOpen]= useState(false);
  const [lightbox,   setLightbox]  = useState(null);
  const [deleteModal,setDeleteModal] = useState(null);
  const [payModal,   setPayModal]  = useState(false);
  const fileInputRef = useRef(null);
  const scrollRef    = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await adminApi.get(`/admin/messages?tab=${tab}`);
      setConvos(data.items || []);
      setStats(data.stats || {});
    } catch {}
  }, [tab]);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    adminApi.get("/admin/staff").then(r => setStaffList(r.data.items || [])).catch(() => {});
  }, []);

  const openConvo = async (conv) => {
    setActive(conv);
    setAssignOpen(false);
    setPayModal(false);
    try {
      const { data } = await adminApi.get(`/admin/messages/${conv.conversation_id}`);
      setThread(data.thread || []);
      await adminApi.put(`/admin/messages/${conv.conversation_id}/status`, { status:"read" }).catch(() => {});
      load();
    } catch {}
  };

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [thread]);

  useEffect(() => {
    if (!active) return;
    const t = setInterval(async () => {
      try {
        const { data } = await adminApi.get(`/admin/messages/${active.conversation_id}`);
        setThread(data.thread || []);
      } catch {}
    }, 8000);
    return () => clearInterval(t);
  }, [active]);

  const [isDragging, setIsDragging] = useState(false);

  const processFiles = (fileList) => {
    Array.from(fileList || []).forEach(file => {
      if (attachments.length >= 4) return;
      if (file.size > MAX_FILE_BYTES) { toast.error(`${file.name} > 4MB`); return; }
      const isImg = file.type.startsWith("image/"), isPdf = file.type === "application/pdf";
      if (!isImg && !isPdf) { toast.error("Images or PDFs only"); return; }
      const r = new FileReader();
      r.onload = ev => setAttach(arr => [...arr, { kind:isImg?"image":"pdf", name:file.name, data_url:ev.target.result }]);
      r.readAsDataURL(file);
    });
  };

  const onFile = (e) => {
    processFiles(e.target.files);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const onDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
  const onDragLeave = (e) => { e.preventDefault(); setIsDragging(false); };
  const onDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    processFiles(e.dataTransfer.files);
  };

  const sendReply = async () => {
    if (!reply.trim() && !attachments.length) return;
    setSending(true);
    try {
      await adminApi.post(`/admin/messages/${active.conversation_id}/reply`, {
        body: reply.trim(), attachments,
      });
      setReply(""); setAttach([]);
      const { data } = await adminApi.get(`/admin/messages/${active.conversation_id}`);
      setThread(data.thread || []);
      // Update active with assigned info
      load();
    } catch { toast.error("Failed to send"); }
    finally { setSending(false); }
  };

  const assignTo = async (email, name) => {
    try {
      await adminApi.patch(`/admin/messages/${active.conversation_id}/assign`, {
        staff_email: email || null, staff_name: name || null,
      });
      setActive(prev => ({ ...prev, assigned_to: email, assigned_name: name }));
      setAssignOpen(false);
      toast.success(email ? `Assigned to ${name}` : "Unassigned");
      load();
    } catch { toast.error("Failed to assign"); }
  };

  const markSpam = async (conv, isSpam) => {
    try {
      await adminApi.patch(`/admin/messages/${conv.conversation_id}/spam`, { is_spam: isSpam });
      toast.success(isSpam ? "Marked as spam" : "Removed from spam");
      if (active?.conversation_id === conv.conversation_id) setActive(null);
      load();
    } catch { toast.error("Failed"); }
  };

  const filtered = convos.filter(c =>
    !search ||
    c.name?.toLowerCase().includes(search.toLowerCase()) ||
    c.email?.toLowerCase().includes(search.toLowerCase()) ||
    c.last_body?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="flex rounded-2xl overflow-hidden shadow-sm"
      style={{ height:"calc(100vh - 88px)", border:"1px solid #dde3ef" }}>

      {/* ── LEFT sidebar ────────────────────────────────────────── */}
      <div className="w-80 shrink-0 flex flex-col" style={{ background:"#f8fafc", borderRight:"1px solid #eef2f8" }}>

        {/* Header */}
        <div className="p-4 space-y-3" style={{ borderBottom:"1px solid #eef2f8" }}>
          <div className="flex items-center justify-between">
            <h2 className="font-heading font-bold text-[17px]" style={{ color:"#0f172a" }}>Messages</h2>
            <button onClick={load} className="p-1.5 rounded-lg hover:bg-zinc-200 transition-colors">
              <RefreshCcw className="h-3.5 w-3.5" style={{ color:"#64748b" }}/>
            </button>
          </div>

          {/* Tabs */}
          <div className="flex flex-wrap gap-1">
            {TABS.map(({ key, label, icon: Icon, color }) => {
              const count = key === "unread" ? stats.unread :
                            key === "mine"   ? stats.mine :
                            key === "unassigned" ? stats.unassigned :
                            key === "spam"   ? stats.spam : stats.total;
              return (
                <button key={key} onClick={() => { setTab(key); setActive(null); }}
                  className="flex items-center gap-1 py-1.5 px-2.5 rounded-lg text-[10px] font-bold uppercase tracking-wide transition-all"
                  style={tab === key
                    ? { background: color, color:"#fff" }
                    : { background:"#fff", border:"1px solid #e2e8f0", color:"#64748b" }}>
                  <Icon className="h-3 w-3 mb-0.5"/>
                  {label}
                  {count > 0 && (
                    <span className="mt-0.5 min-w-[14px] h-[14px] px-0.5 rounded-full flex items-center justify-center text-white font-bold"
                      style={{ background: tab === key ? "rgba(255,255,255,0.35)" : color, fontSize:8 }}>
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5" style={{ color:"#94a3b8" }}/>
            <Input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search name, email…"
              className="pl-8 h-8 text-[12px] rounded-xl"
              style={{ background:"#fff", borderColor:"#dde3ef" }}/>
          </div>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto">
          {filtered.length === 0 && (
            <div className="p-8 text-center text-[11px] font-mono uppercase tracking-widest" style={{ color:"#cbd5e1" }}>
              No conversations
            </div>
          )}
          {filtered.map(conv => (
            <button key={conv.conversation_id} onClick={() => openConvo(conv)}
              className="w-full text-left px-3 py-3 transition-all"
              style={{
                borderBottom:"1px solid #f0f4f8",
                background: active?.conversation_id === conv.conversation_id
                  ? "#e0eaff" : conv.unread_count > 0 ? "#fff" : "transparent",
              }}>
              <div className="flex items-start gap-2.5">
                <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 font-bold text-[13px] text-white"
                  style={{ background: conv.unread_count > 0 ? "#e31e24" : "#94a3b8" }}>
                  {(conv.name?.[0] || "?").toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-1">
                    <span className="font-semibold text-[13px] truncate" style={{ color:"#0f172a" }}>
                      {conv.name || "Unknown"}
                    </span>
                    <span className="text-[9px] font-mono shrink-0" style={{ color:"#94a3b8" }}>
                      {timeAgo(conv.created_at)}
                    </span>
                  </div>
                  <div className="text-[11px] truncate" style={{ color:"#64748b" }}>{conv.email}</div>
                  <div className="text-[11px] truncate mt-0.5 italic" style={{ color:"#94a3b8" }}>{conv.last_body}</div>
                  <div className="flex items-center gap-1 mt-1 flex-wrap">
                    {conv.unread_count > 0 && (
                      <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full text-white"
                        style={{ background:"#e31e24" }}>{conv.unread_count} new</span>
                    )}
                    {conv.assigned_name && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded-full font-mono"
                        style={{ background:"#eff6ff", color:"#1d4ed8", border:"1px solid #bfdbfe" }}>
                        → {conv.assigned_name.split(" ")[0]}
                      </span>
                    )}
                    {!conv.assigned_to && !conv.is_spam && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded-full font-mono"
                        style={{ background:"#fffbeb", color:"#92400e", border:"1px solid #fde68a" }}>
                        unassigned
                      </span>
                    )}
                    {conv.is_spam && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded-full font-mono"
                        style={{ background:"#f1f5f9", color:"#64748b", border:"1px solid #cbd5e1" }}>
                        spam
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* ── RIGHT thread ────────────────────────────────────────── */}
      {active ? (
        <div className="flex-1 flex flex-col relative" style={{ background:"#fff", minWidth:0 }}
          onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}
          data-testid="messages-drop-zone">

          {isDragging && (
            <div className="absolute inset-0 z-20 flex items-center justify-center pointer-events-none"
              style={{ background:"rgba(30,91,184,0.08)", border:"2px dashed #1e5bb8" }}>
              <div className="px-5 py-3 rounded-xl font-heading font-bold text-[14px]"
                style={{ background:"#1e5bb8", color:"#fff" }}>
                Drop image or PDF to attach
              </div>
            </div>
          )}

          {/* Thread header */}
          <div className="px-5 py-3 flex items-center justify-between gap-3"
            style={{ borderBottom:"1px solid #eef2f8" }}>
            <div className="min-w-0">
              <div className="font-heading font-bold text-[15px] truncate" style={{ color:"#0f172a" }}>
                {active.name || "Customer"}
              </div>
              <div className="text-[12px] truncate" style={{ color:"#64748b" }}>
                {active.email}{active.phone && ` · ${active.phone}`}
              </div>
            </div>

            <div className="flex items-center gap-1.5 shrink-0">
              {/* Assign */}
              <div className="relative">
                <button onClick={() => setAssignOpen(v => !v)}
                  className="flex items-center gap-1.5 px-2.5 h-8 rounded-xl text-[11px] font-semibold transition-all"
                  style={active.assigned_to
                    ? { background:"#eff6ff", color:"#1d4ed8", border:"1px solid #bfdbfe" }
                    : { background:"#f5f7fb", color:"#64748b", border:"1px solid #dde3ef" }}>
                  <UserCheck className="h-3.5 w-3.5"/>
                  {active.assigned_name ? active.assigned_name.split(" ")[0] : "Assign"}
                  <ChevronDown className="h-3 w-3"/>
                </button>
                {assignOpen && (
                  <div className="absolute right-0 top-9 z-30 bg-white rounded-xl shadow-xl py-1 min-w-[160px]"
                    style={{ border:"1px solid #dde3ef" }}
                    onClick={e => e.stopPropagation()}>
                    <button onClick={() => assignTo(null, null)}
                      className="w-full text-left px-3 py-2 text-[12px] hover:bg-zinc-50 transition-colors"
                      style={{ color:"#94a3b8" }}>Unassign</button>
                    {staffList.map(s => (
                      <button key={s.id} onClick={() => assignTo(s.email, s.name)}
                        className="w-full text-left px-3 py-2 text-[12px] hover:bg-zinc-50 transition-colors"
                        style={{ color:"#0f172a" }}>
                        {s.name}
                        {s.email === active.assigned_to && " ✓"}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Pay link */}
              <button onClick={() => setPayModal(true)} title="Send payment link"
                className="p-2 rounded-xl transition-all"
                style={{ background:"#f0fdf4", color:"#15803d", border:"1px solid #86efac" }}>
                <Link2 className="h-4 w-4"/>
              </button>

              {/* Spam */}
              <button onClick={() => markSpam(active, !active.is_spam)}
                title={active.is_spam ? "Remove from spam" : "Mark as spam"}
                className="p-2 rounded-xl transition-all"
                style={active.is_spam
                  ? { background:"#fef2f2", color:"#e31e24", border:"1px solid #fca5a5" }
                  : { background:"#f5f7fb", color:"#94a3b8", border:"1px solid #dde3ef" }}>
                <AlertOctagon className="h-4 w-4"/>
              </button>

              {/* Delete */}
              <button onClick={() => setDeleteModal(active)} title="Delete conversation"
                className="p-2 rounded-xl transition-all"
                style={{ background:"#fef2f2", color:"#e31e24", border:"1px solid #fca5a5" }}>
                <Trash2 className="h-4 w-4"/>
              </button>
            </div>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4"
            style={{ background:"#f9fafb" }}>
            {thread.length === 0 && (
              <div className="text-center py-10 text-[12px]" style={{ color:"#94a3b8" }}>No messages yet</div>
            )}
            {thread.map(m => <Bubble key={m.id} m={m} onImageClick={setLightbox}/>)}
          </div>

          {/* Attachment preview */}
          {attachments.length > 0 && (
            <div className="px-4 py-2 flex gap-2 flex-wrap" style={{ borderTop:"1px solid #eef2f8" }}>
              {attachments.map((a, i) => (
                <div key={i} className="relative rounded-lg overflow-hidden"
                  style={{ width:48, height:48, border:"1px solid #dde3ef", background:"#f5f7fb" }}>
                  {a.kind === "image"
                    ? <img src={a.data_url} alt="" className="w-full h-full object-cover"/>
                    : <div className="w-full h-full flex items-center justify-center"><FileText className="h-5 w-5" style={{ color:"#e31e24" }}/></div>}
                  <button type="button" onClick={() => setAttach(arr => arr.filter((_, j) => j !== i))}
                    className="absolute top-0 right-0 w-4 h-4 flex items-center justify-center text-white"
                    style={{ background:"rgba(0,0,0,0.7)", borderBottomLeftRadius:4 }}>
                    <X className="h-2.5 w-2.5"/>
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Reply box */}
          <div className="p-3" style={{ borderTop:"1px solid #eef2f8" }}>
            <div className="flex gap-2 items-end">
              <textarea value={reply} onChange={e => setReply(e.target.value)}
                onKeyDown={e => { if (e.key==="Enter" && !e.shiftKey) { e.preventDefault(); sendReply(); }}}
                placeholder="Reply… (Enter to send, Shift+Enter for new line)"
                rows={2} className="flex-1 resize-none rounded-xl border px-3 py-2 text-[13px] outline-none"
                style={{ borderColor:"#dde3ef", color:"#0f172a", background:"#fafafa" }}/>
              <div className="flex flex-col gap-1 shrink-0">
                <button type="button" onClick={() => fileInputRef.current?.click()}
                  className="p-2 rounded-xl transition-all"
                  style={{ background:"#f5f7fb", border:"1px solid #dde3ef", color:"#64748b" }}>
                  <Paperclip className="h-4 w-4"/>
                </button>
                <button onClick={sendReply} disabled={sending || (!reply.trim() && !attachments.length)}
                  className="p-2 rounded-xl disabled:opacity-40 transition-all"
                  style={{ background:"#0b1a3b", color:"#ffffff" }}>
                  <Send className="h-4 w-4"/>
                </button>
              </div>
            </div>
            <input ref={fileInputRef} type="file" multiple accept="image/*,.pdf" className="hidden" onChange={onFile}/>
          </div>
        </div>
      ) : (
        <div className="flex-1 flex items-center justify-center" style={{ background:"#f9fafb" }}>
          <div className="text-center">
            <MessageSquare className="h-14 w-14 mx-auto mb-3" style={{ color:"#e2e8f0" }}/>
            <div className="font-heading font-bold text-[16px]" style={{ color:"#94a3b8" }}>Select a conversation</div>
            <div className="text-[12px] mt-1" style={{ color:"#cbd5e1" }}>Pick one from the left to start replying</div>
          </div>
        </div>
      )}

      {/* Lightbox */}
      {lightbox && <Lightbox src={lightbox} onClose={() => setLightbox(null)}/>}

      {/* Modals */}
      {deleteModal && (
        <DeleteModal conv={deleteModal} onClose={() => setDeleteModal(null)}
          onDeleted={() => { setDeleteModal(null); setActive(null); load(); }}/>
      )}
      {payModal && active && (
        <PayLinkModal convId={active.conversation_id} onClose={() => setPayModal(false)}
          onSent={() => {
            adminApi.get(`/admin/messages/${active.conversation_id}`).then(r => setThread(r.data.thread || []));
          }}/>
      )}

      {/* Click outside to close assign dropdown */}
      {assignOpen && (
        <div className="fixed inset-0 z-20" onClick={() => setAssignOpen(false)}/>
      )}
    </div>
  );
}